import React, { useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import * as Sentry from "@sentry/react";
import { auth } from "@/auth/firebase";
import { AuthContext } from "@/auth/auth-context";
import { getAuthUser } from "@/requests/gen/react-query/user";
import { AuthUser } from "@/requests/gen/react-query/fastAPI.schemas";
import LoadingScreen from "@/routes/-loading";
import { NotificationPermission } from "@/components/universal/notification-permission";
import { Button } from "@/components/ui/button";
import { isSupported } from "firebase/messaging";

// Define the AuthProvider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setLoading] = React.useState(true);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [authUser, setAuthUser] = React.useState<AuthUser | null>(null);
  const [isMessagingSupported, setIsMessagingSupported] = React.useState(false);

  // Listen for changes in the user state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          // Get the user permissions and entities from the backend
          const authUser = await getAuthUser();
          setAuthUser(authUser);
          Sentry.setUser({
            id: user.uid,
            email: user.email ?? undefined,
            username: user.displayName ?? undefined,
          });
        } else {
          setAuthUser(null);
          Sentry.setUser(null);
        }
        setLoadFailed(false);
      } catch (error) {
        // Never hang on the loading screen if /me fails; surface a retryable error.
        console.error("Failed to load the current user", error);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  // Check if messaging is supported
  useEffect(() => {
    const checkMessagingSupport = async () => {
      const supported = await isSupported();
      setIsMessagingSupported(supported);
    };
    checkMessagingSupport();
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (loadFailed) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center gap-4 text-center px-4">
        <p className="text-muted-foreground">
          We ran into a problem loading your account. Please try again.
        </p>
        <Button onClick={() => window.location.reload()}>Try again</Button>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ authUser, setAuthUser }}>
      {!isLoading && children}
      {isMessagingSupported && <NotificationPermission />}
    </AuthContext.Provider>
  );
}
export { AuthContext };
